import { OVERSIGHT } from './roles'
export const naira = (n) =>
  '₦' + Number(n ?? 0).toLocaleString('en-NG', { maximumFractionDigits: 2 })

// business date in Africa/Lagos regardless of device timezone
export const lagosToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Lagos' }).format(new Date())

export const tierLabel = { general: 'Standard', lounge: 'Lounge', staff: 'Staff' }
export const methodLabel = { pos: 'POS', cash: 'Cash', credit: 'Credit', transfer: 'Transfer' }

// Calendar-date arithmetic done ENTIRELY IN UTC, so no device timezone
// can shift the result. The previous addDays parsed the date at LOCAL
// midnight and then converted to UTC to print it — on a Lagos phone
// (UTC+1) local midnight is 23:00 the previous day in UTC, so every call
// came back one day EARLY: addDays('2026-10-05', 1) returned
// '2026-10-05'. That made the default check-out on every new booking the
// same day as check-in, and shortened the "rooms sold this month" window
// by a day. It only showed on UTC+ devices, which is why testing in UTC
// never caught it.
export const addDays = (iso, n) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

// N days before the Lagos business date — matches lagos_today() - N in
// the database exactly, so app-side windows never disagree with what
// RLS actually permits.
// n days before `from` (default today). The optional anchor exists for
// pages where the date is a control rather than always "now" — Daily
// Sales anchors its prior-day snapshot to the date being browsed.
export const lagosDaysAgo = (n, from) => addDays(from || lagosToday(), -n)

// Who a sale is attributed to for display: the person it was
// recorded on behalf of, if any, with a note on who actually typed
// it — otherwise just whoever entered it themselves. One shared
// definition so the Sales list and Daily Sales history can't drift
// apart on this.
export const whoRecorded = (r) =>
  r.stood_in_for?.full_name
    ? `${r.stood_in_for.full_name} (recorded by ${r.recorder?.full_name || 'unknown'})`
    : (r.recorder?.full_name || 'unknown')

// Payment method(s) for a sale row, for list display. A sale can be
// split across methods — shown as "Split: POS + Cash" rather than
// picking one arbitrarily. Unpaid (no sale_payments row at all) is
// named plainly rather than left blank, since that's exactly the
// thing worth noticing on a list.
export const paymentSummary = (r) => {
  const pays = r.sale_payments || []
  if (!pays.length) return 'Unpaid'
  if (pays.length === 1) return methodLabel[pays[0].method] || pays[0].method
  return 'Split: ' + pays.map(p => methodLabel[p.method] || p.method).join(' + ')
}

// Departments that can fulfil an order — real sales points, plus
// Restaurant specifically (not itself flagged as a sales point in the
// schema, but exactly what a restaurant order needs). Excludes
// Housekeeping, Others, and the store itself — nothing there should
// ever be sold or charged to a guest. One definition shared by the
// room-charge flow and the restaurant-order flow so they can't drift.
export const orderableLocations = (allLocations) =>
  (allLocations || []).filter(l => !l.is_store && (l.is_sales_point || /restaurant/i.test(l.name)))

// Which department a room charge belongs to.
//
// MUST stay identical to the coalesce in department_room_charges
// (migration 503) — the same line has to land in the same department
// whether the figure comes from SQL or from here, or a department's day
// list and its Charged to rooms tracker disagree.
//
// location_id when the line carries one. Otherwise category, which can
// only ever name Restaurant or Minimart — 'drink' is shared by every bar
// and so identifies nothing. A typed bar line from before migration 496
// has neither, and gets null: shown as "no department" rather than
// guessed at, which is what put OpenBar's charges on MainBar.
export const roomChargeDept = (line, allLocations) => {
  if (line?.location_id) return line.location_id
  const find = (re) => (allLocations || []).find(l => re.test(l.name || ''))?.id || null
  if (line?.category === 'food') return find(/restaurant/i)
  if (line?.category === 'minimart') return find(/minimart/i)
  return null
}

// Which category a charge from this department falls under. Used when
// SAVING; roomChargeDept above is the reverse, used when reading. A typed
// line used to be hardcoded 'food' whatever department made it, which is
// why a typed bar charge was filed as Restaurant food.
export const chargeCategoryFor = (locationName) =>
  /restaurant/i.test(locationName || '') ? 'food'
    : /minimart/i.test(locationName || '') ? 'minimart'
      : 'drink'

// Mirrors the database's own generated column exactly (guests.name_key
// is GENERATED ALWAYS using this identical pattern) — needed client-side
// because a generated column can't be searched against directly; this
// computes the same key to look an existing guest up by.
export const nameKey = (name) =>
  String(name || '').toLowerCase()
    .replace(/^\s*(mr|mrs|ms|miss|dr|chief|engr|elder|pastor|prof|alhaji|hajia)\.?\s+/, '')
    .replace(/[^a-z0-9]+/g, '')

export const nightsBetween = (from, to) =>
  Math.max(1, Math.round((new Date(to) - new Date(from)) / 864e5))

const CYCLE_LABELS = { one_off: 'One-off', daily: 'Daily', monthly: 'Monthly', pr: 'PR (free)' }
const CYCLE_ORDER = ['one_off', 'daily', 'monthly', 'pr']
// Which billing cycles a branch actually offers — confirmed directly
// against the live database rather than assumed: Nnewi has no
// 'monthly', Awka does. Falls back to all four if the branch's own
// list hasn't loaded yet.
export const cyclesFor = (allowedCycles) => {
  const allowed = allowedCycles || CYCLE_ORDER
  return CYCLE_ORDER.filter(c => allowed.includes(c)).map(value => ({ value, label: CYCLE_LABELS[value] }))
}

// Translates the specific Postgres errors check-in can actually hit
// into something a person can act on, rather than a raw constraint
// name. Matches the exact constraints confirmed on the live stays
// table (stays_one_live_per_room, stay_dates_ordered).
export function friendlyStayError(error) {
  if (!error) return null
  const msg = error.message || String(error)
  if (error.code === '23505' && msg.includes('stays_one_live_per_room')) {
    return 'That room already has a guest checked in or reserved. Check them out first, or pick another room.'
  }
  if (error.code === '23514' && msg.includes('stay_dates_ordered')) {
    return 'The check-out date cannot be before the check-in date.'
  }
  // 23P01 is the exclusion constraint stays_no_date_overlap. It had no
  // translation, so it reached the desk as raw Postgres — "conflicting
  // key value violates exclusion constraint" — which says nothing to
  // someone holding a key card (29 October 2026). It fires on a
  // same-day turnover and on extending a stay into a booking behind it,
  // which is exactly what a busy morning does.
  if (error.code === '23P01' || msg.includes('stays_no_date_overlap')) {
    return 'Those dates clash with another booking on that room. '
      + 'Check the dates, or pick a room that is free for the whole stay.'
  }
  if (error.code === '42501' || msg.includes('row-level security')) {
    return 'You do not have permission to do that for this branch.'
  }
  return msg
}

// Clock time of an actual check-in, in Lagos time. Returns '' when
// the stay predates the checked_in_at column so callers can simply
// omit it rather than print a fabricated time.
// The same instant as lagosTime, but as an <input type="time"> wants
// it: 24-hour HH:MM, Lagos. lagosTime is for reading ("2:00 PM"); this
// is for editing, and the two must not be confused — a time input fed
// "2:00 PM" simply comes up empty (24 October 2026).
export const lagosHHMM = (ts) => !ts ? '' : new Date(ts).toLocaleTimeString('en-GB', {
  timeZone: 'Africa/Lagos', hour: '2-digit', minute: '2-digit', hour12: false,
})

export const lagosTime = (ts) => !ts ? '' : new Date(ts).toLocaleTimeString('en-NG', {
  timeZone: 'Africa/Lagos', hour: '2-digit', minute: '2-digit', hour12: true,
})

// Who may see arrival/departure clock times on guest lists. Front
// desk and bar staff see the DATES as before; only these roles see
// the stamped time. 'storekeeper' is the Store Manager.
export const SEES_STAY_TIMES = OVERSIGHT
export const seesStayTimes = (role) => SEES_STAY_TIMES.includes(role)

// Which department a screen should open on.
//
// Every department-chip screen used to do `staff.default_location_id ||
// list[0]`, trusting the default blindly. That breaks the moment the
// default isn't in that screen's list — e.g. a store manager whose
// default is the Store, on a screen whose list excludes stores. The page
// then opened on a department that doesn't exist on it, and its own
// "reset if invalid" logic reset straight back to the same bad value.
//
// Order: the person's default, if it's on this screen; else the first
// department they're actually assigned to that's on this screen; else
// the first on the list.
export const startingDept = (list, staff, own = []) => {
  const ids = new Set((list || []).map(l => l.id))
  if (staff?.default_location_id && ids.has(staff.default_location_id)) return staff.default_location_id
  const mine = (own || []).find(l => ids.has(l.id))
  if (mine) return mine.id
  return list?.[0]?.id ?? null
}

// A figure and the words beside it must describe the SAME quantity.
//
// The fault this exists to stop: a line showing what is LEFT while the
// words beneath describe what was CHARGED. "5 nights at ₦35,000" sat
// over ₦30,000, and ₦5,000 of a ₦12,000 pasta read as an unpaid meal
// nobody could account for — both figures correct, both descriptions
// about something else.
//
// So anything part-paid says so, in one phrasing, from one place.
// Returns null when nothing has been paid, where naming the whole is
// honest because the whole is what is owed.
export const partPaidNote = (charged, left) => {
  const paid = Number(charged || 0) - Number(left || 0)
  if (!(paid > 0.009)) return null
  return `${naira(paid)} of ${naira(charged)} already paid`
}
